/**
 * ============================================================================
 * VALIDATION HELPERS
 * ============================================================================
 */

/**
 * Check if a field contains a value (not null, undefined, empty string, or empty array)
 * @param {any} value - Value to check
 * @returns {boolean} True if value exists and is not empty
 * @example
 * fieldContainsValue("hello") // true
 * fieldContainsValue("") // false
 * fieldContainsValue([]) // false
 * fieldContainsValue(0) // true
 */
export const fieldContainsValue = (value) => {
  if (value === 0 || value === "0") {
    return true;
  }
  if (!value || value === null || value === "" || (Array?.isArray(value) && value?.length <= 0)) {
    return false;
  }
  return true;
};

/**
 * Validate required field
 * @param {any} value - Field value
 * @param {boolean} required - Is field required
 * @returns {string|null} Error message or null
 * @example
 * validateRequired("", true) // "This field is required"
 * validateRequired("value", true) // null
 */
export const validateRequired = (value, required) => {
  if (!required) return null;

  if (value === null || value === undefined || value === "") {
    return "This field is required";
  }

  if (Array.isArray(value) && value.length === 0) {
    return "Please select at least one option";
  }

  return null;
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid email
 * @example
 * validateEmail("test@example.com") // true
 * validateEmail("invalid") // false
 */
export const validateEmail = (email) => {
  if (!email || typeof email !== "string") return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number (10 digits)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid phone number
 * @example
 * validatePhone("9876543210") // true
 * validatePhone("123") // false
 */
export const validatePhone = (phone) => {
  if (!phone) return false;
  const phoneRegex = /^[1-9]\d{9}$/;
  return phoneRegex.test(String(phone).replace(/\D/g, ""));
};

/**
 * Validate URL format
 * @param {string} url - URL to validate
 * @returns {boolean} True if valid URL
 * @example
 * validateUrl("https://example.com") // true
 * validateUrl("invalid") // false
 */
export const validateUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * ============================================================================
 * TYPE CHECKING HELPERS
 * ============================================================================
 */

/**
 * Check if value is an array
 * @param {any} value - Value to check
 * @returns {boolean} True if array
 */
export const isArray = (value) => Array.isArray(value);

/**
 * Check if value is an object (and not array or null)
 * @param {any} value - Value to check
 * @returns {boolean} True if object
 */
export const isObject = (value) => {
  return value !== null && typeof value === "object" && !Array.isArray(value);
};

/**
 * Check if value is a function
 * @param {any} value - Value to check
 * @returns {boolean} True if function
 */
export const isFunction = (value) => typeof value === "function";

/**
 * Check if value is a string
 * @param {any} value - Value to check
 * @returns {boolean} True if string
 */
export const isString = (value) => typeof value === "string";

/**
 * Check if value is a number
 * @param {any} value - Value to check
 * @returns {boolean} True if number
 */
export const isNumber = (value) => typeof value === "number" && !isNaN(value);

/**
 * Check if value is empty (null, undefined, empty string, empty array, empty object)
 * @param {any} value - Value to check
 * @returns {boolean} True if empty
 */
export const isEmpty = (value) => {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (isObject(value) && Object.keys(value).length === 0) return true;
  return false;
};

/**
 * Get data type of value
 * @param {any} value - Value to check
 * @returns {string} Data type
 * @example
 * getDataType([]) // "array"
 * getDataType({}) // "object"
 * getDataType(null) // "null"
 */
export const getDataType = (value) => {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  return typeof value;
};

/**
 * ============================================================================
 * ARRAY HELPERS
 * ============================================================================
 */

/**
 * Get unique values from array
 * @param {Array} arr - Array to process
 * @returns {Array} Array with unique values
 * @example
 * getUniqueValues([1, 2, 2, 3]) // [1, 2, 3]
 */
export const getUniqueValues = (arr) => {
  if (!Array.isArray(arr)) return [];
  return [...new Set(arr)];
};

/**
 * Group array items by key
 * @param {Array} arr - Array to group
 * @param {string|Function} key - Key to group by or function to extract key
 * @returns {Object} Grouped object
 * @example
 * groupBy([{type: 'a', val: 1}, {type: 'b', val: 2}], 'type')
 * // { a: [{type: 'a', val: 1}], b: [{type: 'b', val: 2}] }
 */
export const groupBy = (arr, key) => {
  if (!Array.isArray(arr)) return {};
  return arr.reduce((acc, item) => {
    const groupKey = typeof key === "function" ? key(item) : item[key];
    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(item);
    return acc;
  }, {});
};

/**
 * Flatten nested array
 * @param {Array} arr - Array to flatten
 * @param {number} depth - Flattening depth (default: Infinity)
 * @returns {Array} Flattened array
 * @example
 * flattenArray([1, [2, [3]]]) // [1, 2, 3]
 */
export const flattenArray = (arr, depth = Infinity) => {
  if (!Array.isArray(arr)) return [];
  return arr.flat(depth);
};

/**
 * Chunk array into smaller arrays
 * @param {Array} arr - Array to chunk
 * @param {number} size - Chunk size
 * @returns {Array} Array of chunks
 * @example
 * chunkArray([1, 2, 3, 4, 5], 2) // [[1, 2], [3, 4], [5]]
 */
export const chunkArray = (arr, size) => {
  if (!Array.isArray(arr) || size <= 0) return [];
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

/**
 * Remove duplicates from array of objects by key
 * @param {Array} arr - Array to process
 * @param {string} key - Key to check for duplicates
 * @returns {Array} Array without duplicates
 * @example
 * removeDuplicatesByKey([{id: 1}, {id: 1}, {id: 2}], 'id')
 * // [{id: 1}, {id: 2}]
 */
export const removeDuplicatesByKey = (arr, key) => {
  if (!Array.isArray(arr)) return [];
  const seen = new Set();
  return arr.filter((item) => {
    const value = item[key];
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

/**
 * Sort array of objects by key
 * @param {Array} arr - Array to sort
 * @param {string} key - Key to sort by
 * @param {string} order - Sort order: 'asc' | 'desc' (default: 'asc')
 * @returns {Array} Sorted array
 */
export const sortByKey = (arr, key, order = "asc") => {
  if (!Array.isArray(arr)) return [];
  const sorted = [...arr].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];
    if (aVal < bVal) return order === "asc" ? -1 : 1;
    if (aVal > bVal) return order === "asc" ? 1 : -1;
    return 0;
  });
  return sorted;
};

/**
 * Find item in array by key-value pair
 * @param {Array} arr - Array to search
 * @param {string} key - Key to search
 * @param {any} value - Value to match
 * @returns {any|undefined} Found item or undefined
 */
export const findItemByKey = (arr, key, value) => {
  if (!Array.isArray(arr)) return undefined;
  return arr.find((item) => item[key] === value);
};

/**
 * Filter array by multiple conditions
 * @param {Array} arr - Array to filter
 * @param {Object} filters - Filter conditions object
 * @returns {Array} Filtered array
 * @example
 * filterByConditions([{type: 'a', status: 'active'}, {type: 'b', status: 'inactive'}], {type: 'a'})
 * // [{type: 'a', status: 'active'}]
 */
export const filterByConditions = (arr, filters) => {
  if (!Array.isArray(arr) || !isObject(filters)) return [];
  return arr.filter((item) => {
    return Object.keys(filters).every((key) => {
      const filterValue = filters[key];
      if (Array.isArray(filterValue)) {
        return filterValue.includes(item[key]);
      }
      return item[key] === filterValue;
    });
  });
};

/**
 * ============================================================================
 * OBJECT HELPERS
 * ============================================================================
 */

/**
 * Deep clone an object
 * @param {any} obj - Object to clone
 * @returns {any} Cloned object
 * @example
 * deepClone({a: {b: 1}}) // {a: {b: 1}}
 */
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== "object") return obj;
  if (obj instanceof Date) return new Date(obj.getTime());
  if (Array.isArray(obj)) return obj.map((item) => deepClone(item));

  const cloned = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      cloned[key] = deepClone(obj[key]);
    }
  }
  return cloned;
};

/**
 * Deep merge objects
 * @param {Object} target - Target object
 * @param {...Object} sources - Source objects to merge
 * @returns {Object} Merged object
 * @example
 * deepMerge({a: 1}, {b: 2}, {a: 3}) // {a: 3, b: 2}
 */
export const deepMerge = (target, ...sources) => {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        deepMerge(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return deepMerge(target, ...sources);
};

/**
 * Get nested value from object by path
 * @param {Object} obj - Object to search
 * @param {string} path - Dot-separated path (e.g., 'user.profile.name')
 * @param {any} defaultValue - Default value if not found
 * @returns {any} Value at path or default
 * @example
 * getNestedValue({user: {profile: {name: 'John'}}}, 'user.profile.name')
 * // 'John'
 */
export const getNestedValue = (obj, path, defaultValue = undefined) => {
  if (!obj || !path) return defaultValue;
  const keys = path.split(".");
  let result = obj;
  for (const key of keys) {
    if (result === null || result === undefined) return defaultValue;
    result = result[key];
  }
  return result !== undefined ? result : defaultValue;
};

/**
 * Filter table rows by search value on a column (case-insensitive includes).
 * @param {Object} opts - Options
 * @param {string} opts.value - Search string
 * @param {string} opts.key - Column key to match
 * @param {Array} opts.data - Rows to filter
 * @param {Array} opts.columns - Column definitions (must have key and dataIndex)
 * @param {boolean} [opts.sanitizedSearch] - If true, strip _ - . and space before matching
 * @returns {Array} Filtered rows
 */
export const filterTableRowsBySearch = ({
  value = "",
  key,
  data = [],
  columns = [],
  sanitizedSearch = false,
}) => {
  const valueStr = value?.toString()?.toLowerCase();
  const column = columns?.find((item) => item.key === key);
  if (!column) return data;
  if (!value || value === "") return data;
  const dataIndex = column.dataIndex;
  const path = Array.isArray(dataIndex) ? dataIndex.join(".") : dataIndex;
  if (!path) return data;

  return data.filter((item) => {
    const targetValue = getNestedValue(item, path);
    if (targetValue === null || targetValue === undefined) return false;
    if (sanitizedSearch) {
      const normalizedSearch = valueStr
        ?.replaceAll("_", "")
        ?.replaceAll("-", "")
        ?.replaceAll(".", "")
        ?.replaceAll(" ", "")
        ?.toLowerCase();
      const normalizedTarget = targetValue
        ?.toString()
        ?.replaceAll("_", "")
        ?.replaceAll("-", "")
        ?.replaceAll(".", "")
        ?.replaceAll(" ", "")
        ?.toLowerCase();
      return normalizedTarget?.includes(normalizedSearch);
    }
    return targetValue?.toString()?.toLowerCase()?.includes(valueStr);
  });
};

/**
 * Set nested value in object by path
 * @param {Object} obj - Object to modify
 * @param {string} path - Dot-separated path
 * @param {any} value - Value to set
 * @returns {Object} Modified object
 */
export const setNestedValue = (obj, path, value) => {
  if (!obj || !path) return obj;
  const keys = path.split(".");
  const lastKey = keys.pop();
  const target = keys.reduce((acc, key) => {
    if (!acc[key] || !isObject(acc[key])) acc[key] = {};
    return acc[key];
  }, obj);
  target[lastKey] = value;
  return obj;
};

/**
 * Pick specific keys from object
 * @param {Object} obj - Object to pick from
 * @param {Array<string>} keys - Keys to pick
 * @returns {Object} Object with only picked keys
 * @example
 * pick({a: 1, b: 2, c: 3}, ['a', 'c']) // {a: 1, c: 3}
 */
export const pick = (obj, keys) => {
  if (!isObject(obj) || !Array.isArray(keys)) return {};
  return keys.reduce((acc, key) => {
    if (key in obj) acc[key] = obj[key];
    return acc;
  }, {});
};

/**
 * Omit specific keys from object
 * @param {Object} obj - Object to omit from
 * @param {Array<string>} keys - Keys to omit
 * @returns {Object} Object without omitted keys
 * @example
 * omit({a: 1, b: 2, c: 3}, ['b']) // {a: 1, c: 3}
 */
export const omit = (obj, keys) => {
  if (!isObject(obj) || !Array.isArray(keys)) return obj;
  const result = { ...obj };
  keys.forEach((key) => delete result[key]);
  return result;
};

/**
 * Remove empty values from object
 * @param {Object} obj - Object to clean
 * @returns {Object} Object without empty values
 */
export const removeEmptyValues = (obj) => {
  if (!isObject(obj)) return obj;
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (!isEmpty(value)) {
      cleaned[key] = value;
    }
  }
  return cleaned;
};

/**
 * Get object keys excluding specified keys
 * @param {Object} obj - Object
 * @param {Array<string>} excludeKeys - Keys to exclude
 * @returns {Array<string>} Filtered keys
 */
export const getObjectKeys = (obj, excludeKeys = []) => {
  if (!isObject(obj)) return [];
  return Object.keys(obj).filter((key) => !excludeKeys.includes(key));
};

/**
 * Check if two objects are deeply equal
 * @param {any} a - First value
 * @param {any} b - Second value
 * @returns {boolean} True if deeply equal
 */
export const isDeepEqual = (a, b) => {
  if (a === b) return true;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((val, i) => isDeepEqual(val, b[i]));
  }
  if (isObject(a) && isObject(b)) {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every((k) => isDeepEqual(a[k], b[k]));
  }
  return false;
};

/**
 * Get changed entries between two objects
 * @param {Object} initialObj - Initial object
 * @param {Object} payloadObj - Updated object
 * @param {Array<string>} preserveKeys - Keys to always include
 * @returns {Object} Object with only changed values
 */
export const getChangedEntries = (initialObj = {}, payloadObj = {}, preserveKeys = []) => {
  const changed = {};
  for (const key in payloadObj) {
    if (preserveKeys.includes(key)) {
      changed[key] = payloadObj[key];
    } else if (!isDeepEqual(initialObj[key], payloadObj[key])) {
      changed[key] = payloadObj[key];
    }
  }
  return removeEmptyValues(changed);
};

/**
 * Flatten nested object
 * @param {Object} obj - Object to flatten
 * @param {string} prefix - Key prefix (internal use)
 * @returns {Object} Flattened object
 * @example
 * flattenObject({a: {b: 1}}) // {'a.b': 1}
 */
export const flattenObject = (obj, prefix = "") => {
  const flattened = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const newKey = prefix ? `${prefix}.${key}` : key;
      if (isObject(obj[key]) && !Array.isArray(obj[key])) {
        Object.assign(flattened, flattenObject(obj[key], newKey));
      } else {
        flattened[newKey] = obj[key];
      }
    }
  }
  return flattened;
};

/**
 * ============================================================================
 * DEBOUNCE & THROTTLE HELPERS
 * ============================================================================
 */

/**
 * Debounce function - delays execution until after wait time
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 * @example
 * const debouncedSearch = debounce(searchFunction, 300);
 */
export const debounce = (func, wait = 300) => {
  if (!isFunction(func)) throw new Error("First argument must be a function");
  let timeoutId;
  return function debounced(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), wait);
  };
};

/**
 * Throttle function - limits execution to once per wait time
 * @param {Function} func - Function to throttle
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Throttled function
 * @example
 * const throttledScroll = throttle(handleScroll, 100);
 */
export const throttle = (func, wait = 300) => {
  if (!isFunction(func)) throw new Error("First argument must be a function");
  let lastCall = 0;
  return function throttled(...args) {
    const now = Date.now();
    if (now - lastCall >= wait) {
      lastCall = now;
      func.apply(this, args);
    }
  };
};

/**
 * ============================================================================
 * STORAGE HELPERS
 * ============================================================================
 */

/**
 * Get value from localStorage
 * @param {string} key - Storage key
 * @param {any} defaultValue - Default value if not found
 * @returns {any} Stored value or default
 */
export const getLocalStorage = (key, defaultValue = null) => {
  if (typeof window === "undefined") return defaultValue;
  try {
    const item = window.localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
};

/**
 * Set value in localStorage
 * @param {string} key - Storage key
 * @param {any} value - Value to store
 * @returns {boolean} True if successful
 */
export const setLocalStorage = (key, value) => {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
};

/**
 * Remove value from localStorage
 * @param {string} key - Storage key
 * @returns {boolean} True if successful
 */
export const removeLocalStorage = (key) => {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

/**
 * Clear all localStorage
 * @returns {boolean} True if successful
 */
export const clearLocalStorage = () => {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.clear();
    return true;
  } catch {
    return false;
  }
};

/**
 * Get value from sessionStorage
 * @param {string} key - Storage key
 * @param {any} defaultValue - Default value if not found
 * @returns {any} Stored value or default
 */
export const getSessionStorage = (key, defaultValue = null) => {
  if (typeof window === "undefined") return defaultValue;
  try {
    const item = window.sessionStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
};

/**
 * Set value in sessionStorage
 * @param {string} key - Storage key
 * @param {any} value - Value to store
 * @returns {boolean} True if successful
 */
export const setSessionStorage = (key, value) => {
  if (typeof window === "undefined") return false;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
};

/**
 * ============================================================================
 * URL & QUERY PARAMETER HELPERS
 * ============================================================================
 */

/**
 * Get query parameters from URL
 * @param {string} search - Query string (default: window.location.search)
 * @returns {Object} Object with query parameters
 * @example
 * getQueryParams('?page=1&limit=10') // {page: '1', limit: '10'}
 */
export const getQueryParams = (search = null) => {
  const queryString = search || (typeof window !== "undefined" ? window.location.search : "");
  const params = new URLSearchParams(queryString);
  const result = {};
  for (const [key, value] of params.entries()) {
    result[key] = value;
  }
  return result;
};

/**
 * Set query parameters in URL
 * @param {Object} params - Query parameters object
 * @param {boolean} replace - Use replaceState instead of pushState
 * @returns {void}
 */
export const setQueryParams = (params, replace = false) => {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, String(value));
    }
  });
  const method = replace ? window.history.replaceState : window.history.pushState;
  method.call(window.history, {}, "", url.toString());
};

/**
 * Remove query parameters from URL
 * @param {Array<string>} keys - Keys to remove
 * @returns {void}
 */
export const removeQueryParams = (keys) => {
  if (typeof window === "undefined" || !Array.isArray(keys)) return;
  const url = new URL(window.location.href);
  keys.forEach((key) => url.searchParams.delete(key));
  window.history.pushState({}, "", url.toString());
};

/**
 * ============================================================================
 * ASYNC HELPERS
 * ============================================================================
 */

/**
 * Sleep/delay function
 * @param {number} ms - Milliseconds to wait
 * @returns {Promise} Promise that resolves after delay
 * @example
 * await sleep(1000); // Wait 1 second
 */
export const sleep = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Retry async function with exponential backoff
 * @param {Function} fn - Async function to retry
 * @param {number} retries - Number of retries
 * @param {number} delay - Initial delay in ms
 * @returns {Promise} Result of function
 */
export const retry = async (fn, retries = 3, delay = 1000) => {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 0) throw error;
    await sleep(delay);
    return retry(fn, retries - 1, delay * 2);
  }
};

/**
 * Execute async function with timeout
 * @param {Function} fn - Async function
 * @param {number} timeout - Timeout in ms
 * @returns {Promise} Result or timeout error
 */
export const withTimeout = (fn, timeout) => {
  return Promise.race([
    fn(),
    new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), timeout)),
  ]);
};

/**
 * ============================================================================
 * ERROR HANDLING HELPERS
 * ============================================================================
 */

/**
 * Safely execute function and return result or error
 * @param {Function} fn - Function to execute
 * @param {any} defaultValue - Default value on error
 * @returns {any} Result or default value
 */
export const safeExecute = (fn, defaultValue = null) => {
  try {
    return fn();
  } catch (error) {
    console.warn("Safe execute error:", error);
    return defaultValue;
  }
};

/**
 * Get a human-readable message from any error (generic errors, API responses, strings).
 * Prefers API-style shapes (data.message, data.error, response.data) then standard message/error.
 * @param {any} error - Error object, API response, or string
 * @param {string} [fallback='Something went wrong'] - Message when nothing can be extracted
 * @returns {string} Error message safe to show in UI
 * @example
 * getErrorMessage(new Error('Fail')) // "Fail"
 * getErrorMessage({ data: { message: 'Invalid' } }) // "Invalid"
 * getErrorMessage({ response: { data: { error: 'Not found' } } }) // "Not found"
 */
export const getErrorMessage = (error, fallback = "Something went wrong") => {
  if (error == null) return fallback;
  if (typeof error === "string") return error.trim() || fallback;

  const data = error?.data ?? error?.response?.data;
  const message = data?.message ?? data?.error ?? error?.message ?? error?.error;

  if (message != null && typeof message === "string") return message.trim();
  if (message != null) return String(message);
  return fallback;
};

/**
 * ============================================================================
 * STRING HELPERS
 * ============================================================================
 */

/**
 * Generate random string
 * @param {number} length - String length
 * @returns {string} Random string
 */
export const randomString = (length = 10) => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Generate UUID v4
 * @returns {string} UUID string
 */
export const generateUUID = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const DEVICE_ID_STORAGE_KEY = "new_carrum_device_id";

function generateFingerprint() {
  const data = [
    navigator.userAgent,
    navigator.language,
    screen.width,
    screen.height,
    new Date().getTimezoneOffset(),
    navigator.platform,
  ].join("|");

  return hashString(data);
}

function hashString(str) {
  let hash = 0,
    i,
    chr;

  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }

  return "dev_" + Math.abs(hash);
}

function createDeviceId() {
  const randomId =
    typeof globalThis.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : generateUUID();

  return `${generateFingerprint()}_${randomId}`;
}

/**
 * Returns a persistent device id (stored in localStorage). Used as machine/device identifier
 * for APIs that require device-id. Browsers do not expose MAC address; this combines a
 * browser fingerprint with a stable UUID per browser.
 */

export const getDeviceId = () => {
  try {
    let deviceId = localStorage.getItem(DEVICE_ID_STORAGE_KEY);

    if (!deviceId) {
      deviceId = createDeviceId();
      localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
    }

    return deviceId;
  } catch {
    return createDeviceId();
  }
};

/**
 * ============================================================================
 * NUMBER HELPERS
 * ============================================================================
 */

/**
 * Clamp number between min and max
 * @param {number} value - Value to clamp
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {number} Clamped value
 */
export const clamp = (value, min, max) => {
  return Math.min(Math.max(value, min), max);
};

/**
 * Generate random number between min and max
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {number} Random number
 */
export const randomNumber = (min, max) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Uppercase the first character of a string (remainder unchanged).
 * @param {string} str
 * @returns {string}
 */
export const uppercaseFirstChar = (str) => {
  if (typeof str !== "string") return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * ============================================================================
 * EXPORT ALL HELPERS
 * ============================================================================
 */

export default {
  // Validation
  fieldContainsValue,
  validateRequired,
  validateEmail,
  validatePhone,
  validateUrl,

  // Type checking
  isArray,
  isObject,
  isFunction,
  isString,
  isNumber,
  isEmpty,
  getDataType,

  // Array helpers
  getUniqueValues,
  groupBy,
  flattenArray,
  chunkArray,
  removeDuplicatesByKey,
  sortByKey,
  findItemByKey,
  filterByConditions,

  // Object helpers
  deepClone,
  deepMerge,
  getNestedValue,
  filterTableRowsBySearch,
  setNestedValue,
  pick,
  omit,
  removeEmptyValues,
  getObjectKeys,
  isDeepEqual,
  getChangedEntries,
  flattenObject,

  // Debounce & throttle
  debounce,
  throttle,

  // Storage
  getLocalStorage,
  setLocalStorage,
  removeLocalStorage,
  clearLocalStorage,
  getSessionStorage,
  setSessionStorage,

  // URL helpers
  getQueryParams,
  setQueryParams,
  removeQueryParams,

  // Async helpers
  sleep,
  retry,
  withTimeout,

  // Error handling
  safeExecute,
  getErrorMessage,

  // String helpers
  uppercaseFirstChar,
  randomString,
  generateUUID,
  getDeviceId,

  // Number helpers
  clamp,
  randomNumber,
};
